import { useEffect, useMemo, useRef, useState } from 'react';

import type { ChartData, ChartFields } from '../../types';
import { Plot } from '../Plot';
import { Stars } from '../Stars';
import { Empty } from '../states';
import { cell, compact, exact, humanise, isNumeric, truncate } from '../format';

/**
 * Several ranked lists, side by side, split by one dimension.
 *
 * This is the form behind Superset's Top Charts — Free / Paid / Grossing as
 * three columns off one `top_charts` query, ranked in query order. It is
 * deliberately general rather than a Top-Charts-shaped special case: any result
 * with a small split dimension (segment, tier, platform, store) renders the
 * same way, so the next "three lists next to each other" request is a form
 * choice rather than a new template.
 *
 * Rank comes from row order, not from sorting here. The query already decided
 * the ranking, and re-sorting would silently override a deliberate ORDER BY —
 * the same rule the rest of the chart layer follows.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

/** Beyond this the columns are too narrow to read; the tail is dropped and said so. */
const MAX_COLUMNS = 4;
const MAX_ROWS = 25;
/** A dimension with more distinct values than this is a label, not a split. */
const MAX_SPLIT_CARDINALITY = 6;

const SPLIT_HINT = /^(type|segment|group|tier|band|chart|list|category)$/i;
/** A human-readable label. `app_name`, `title`, `publisher_name`. */
const LABEL_HINT = /(name|title|label)/i;
/**
 * Identifier columns, which are never the right thing to show a reader.
 *
 * This matters more than it looks: `top_charts` returns both `app_id`
 * ("com.naver.linewebtoon") and `app_name` ("WEBTOON: Manga, Comics, Manhwa"),
 * and `app_id` comes first. A label rule that merely looked for "app" picked
 * the bundle id and rendered a chart full of reverse-DNS strings.
 */
const ID_HINT = /(^|_)(id|guid|uuid|key)$/i;
const RATING_HINT = /(rating|score|stars)/i;
const ICON_HINT = /(icon|image|logo|thumb|artwork)/i;
/**
 * Columns worth revealing when a row is opened. Curated rather than
 * "everything else": the payload also carries plumbing like `has_rating` and
 * `star_pct`, which are how the row is drawn, not facts about the app.
 */
const DETAIL_HINT = /(publisher|category|genre|rank|pos|price|release)/i;
/**
 * Long prose, handled separately from the short facts above.
 *
 * `description` used to be a DETAIL_HINT like any other, which was the wrong
 * shape for it: the details row is built for `Category: COMICS` and
 * `Price: 0`, while chart 3 returns 1,400–4,100 characters of store copy per
 * app (median ~2,400). One field dwarfing the other three is why the card had
 * no room left for the ranking it exists to show.
 */
const DESCRIPTION_HINT = /(description|summary|about)/i;
const PLUMBING = /(^has_|_pct$|_url$|^is_)/i;
/** Detail beyond this is a table, not a hover card. */
const MAX_DETAILS = 4;
/**
 * Readable labels for columns whose names are shorthand. `humanise` turns
 * `pos` into "Pos" and `publisher_name` into "Publisher name", which are
 * accurate and not what a reader would call them.
 */
const DETAIL_LABELS: Record<string, string> = {
  pos: 'Rank',
  rank: 'Rank',
  best_rank: 'Best rank',
  publisher_name: 'Publisher',
  publisher_id: 'Publisher',
  app_count: 'Apps',
  review_count: 'Reviews',
};

const text = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

/**
 * Store copy, as text.
 *
 * The `description` column is not plain text — every row on chart 3 carries
 * around 22 literal `<br>` tags. React escapes those, so the card drew the
 * characters `<br>` instead of a line break, which is what made the copy
 * unreadable as well as long.
 *
 * Deliberately NOT rendered as HTML. This is third-party store copy, so
 * `dangerouslySetInnerHTML` on it would be an injection sink in exchange for
 * some markup nobody asked for. Breaks become newlines, any other tag is
 * dropped, and the entities that survive that are decoded — `&amp;` last, so a
 * literal `&lt;` in the copy cannot be decoded twice into a tag.
 */
export const plainText = (raw: string): string =>
  raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)\s*>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

interface Detail {
  label: string;
  value: string;
}

interface Column {
  name: string;
  rows: Array<{
    label: string;
    value: unknown;
    icon?: string;
    details: Detail[];
    description?: string;
  }>;
}

/**
 * One ranked entry, and whatever else is known about it.
 *
 * Opens on CLICK, not on hover. Hover was the obvious choice and the wrong one:
 * the list scrolls inside a fixed-height card, so a row near the bottom expands
 * into space the card does not have, and reaching that content means moving the
 * pointer — which un-hovers the row and closes it again. A row four down could
 * therefore never be read at all.
 *
 * `open` is owned by the parent so the list behaves as an accordion. Two rows
 * of store copy open at once would overflow a dashboard tile just as badly as
 * the un-collapsed description did.
 */
const Row = ({
  row,
  rank,
  asStars,
  open,
  onToggle,
}: {
  row: Column['rows'][number];
  rank: number;
  asStars: boolean;
  open: boolean;
  onToggle: () => void;
}) => {
  const detailRef = useRef<HTMLDivElement>(null);
  const [showAll, setShowAll] = useState(false);
  const hasDetail = row.details.length > 0 || Boolean(row.description);

  useEffect(() => {
    if (!open) {
      // Closing resets the description, so re-opening starts clamped rather
      // than at whatever length it was left at.
      setShowAll(false);
      return;
    }

    // Scroll the DETAIL into view, not the row. A gallery card gives the chart
    // 220px total, so scrolling the row to the top still leaves its detail
    // below the card's clip — which is exactly what made rows past the first
    // unreadable. `nearest` moves the minimum distance, so a detail already
    // visible does not jolt the list.
    //
    // Deferred past the 160ms expand: measuring mid-transition scrolls to where
    // the detail was, not where it lands. The timer is cleared on close and on
    // unmount so a quick click-through cannot scroll after the fact.
    const timer = window.setTimeout(
      () => detailRef.current?.scrollIntoView({ block: 'nearest' }),
      180,
    );
    return () => window.clearTimeout(timer);
  }, [open]);

  const summary = (
    <>
      <span className="ranked__rank">{rank}</span>
      {row.icon && (
        <span
          className="ranked__icon"
          style={{ backgroundImage: `url(${CSS.escape(row.icon)})` }}
          aria-hidden="true"
        />
      )}
      <span className="ranked__name" title={row.label}>
        {truncate(row.label, 28)}
      </span>
      <span className="ranked__value" title={isNumeric(row.value) ? exact(row.value) : ''}>
        {asStars && isNumeric(row.value) ? (
          <Stars value={row.value} />
        ) : isNumeric(row.value) ? (
          compact(row.value)
        ) : (
          cell(row.value)
        )}
      </span>
    </>
  );

  return (
    <li className="ranked__row" data-open={open || undefined}>
      {hasDetail ? (
        /* A real <button>, so the row is keyboard-operable and announces its
           state, rather than a div with a click handler bolted on. */
        <button type="button" className="ranked__summary" aria-expanded={open} onClick={onToggle}>
          {summary}
          <span className="ranked__chev" aria-hidden="true" />
        </button>
      ) : (
        <div className="ranked__summary ranked__summary--static">{summary}</div>
      )}

      {hasDetail && (
        <div className="ranked__detail">
          {/* Exactly ONE grid child, which is what makes the collapse work:
              `grid-template-rows: 0fr` sizes only the first EXPLICIT row, so
              one grid child per fact left facts 2..n in implicit `auto` rows
              that never collapsed. */}
          <div className="ranked__detail-inner" ref={detailRef}>
            {row.details.length > 0 && (
              <dl className="ranked__facts">
                {row.details.map((d) => (
                  <div key={d.label}>
                    <dt>{d.label}</dt>
                    <dd>{d.value}</dd>
                  </div>
                ))}
              </dl>
            )}

            {row.description && (
              <div className="ranked__desc">
                <p className="ranked__desc-text" data-open={showAll || undefined}>
                  {row.description}
                </p>
                <button
                  type="button"
                  className="ranked__more"
                  aria-expanded={showAll}
                  onClick={(e) => {
                    // The row's own toggle sits on an ancestor; without this,
                    // asking for more text would immediately close the row.
                    e.stopPropagation();
                    setShowAll((v) => !v);
                    // Growing the copy moves this control; bring it back into
                    // view rather than leaving the reader with no way to undo.
                    window.setTimeout(
                      () => (e.target as HTMLElement).scrollIntoView({ block: 'nearest' }),
                      0,
                    );
                  }}
                >
                  {showAll ? 'Show less' : 'Show more'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

export const RankedColumns = ({ data }: Props) => {
  /** One open row at a time — see Row. `null` is all closed. */
  const [openRow, setOpenRow] = useState<string | null>(null);

  const scroller = useRef<HTMLDivElement>(null);
  /** Where the reader was before opening a row moved the list for them. */
  const restoreTo = useRef<number | null>(null);

  /**
   * Opening a row scrolls the list so the detail is visible. Closing it has to
   * put the list back, or the reader is left wherever that scroll happened to
   * land — which on a 10-row list means the top of the ranking has silently
   * scrolled away and looks lost.
   *
   * Restoring is deferred past the 160ms collapse: the list is still tall
   * mid-transition, so setting scrollTop now would be clamped against a height
   * that is about to shrink.
   */
  const toggle = (key: string) => {
    setOpenRow((current) => {
      if (current === key) {
        const top = restoreTo.current;
        restoreTo.current = null;
        if (top !== null) {
          window.setTimeout(() => {
            if (scroller.current) scroller.current.scrollTop = top;
          }, 180);
        }
        return null;
      }
      // Only on the first open — switching straight from one row to another
      // must keep the original position, not overwrite it with this one.
      if (current === null) restoreTo.current = scroller.current?.scrollTop ?? 0;
      return key;
    });
  };

  const view = useMemo(() => {
    if (!data.columns.length || !data.rows.length) return null;

    const indexOf = (c: string) => data.columns.indexOf(c);
    const numericAt = (i: number) =>
      data.rows.slice(0, 30).some((r) => isNumeric(r[i]));

    const dimensions = data.columns.filter((_, i) => !numericAt(i));
    const measures = data.columns.filter((_, i) => numericAt(i));

    const distinct = (c: string) =>
      new Set(data.rows.map((r) => text(r[indexOf(c)]))).size;

    // Prefer a column that is named like a split; otherwise the smallest
    // low-cardinality dimension. Falls back to a single unnamed column so the
    // form still renders something useful on a flat list.
    const splitColumn =
      dimensions.find((c) => SPLIT_HINT.test(c) && distinct(c) <= MAX_SPLIT_CARDINALITY) ??
      dimensions
        .filter((c) => distinct(c) > 1 && distinct(c) <= MAX_SPLIT_CARDINALITY)
        .sort((a, b) => distinct(a) - distinct(b))[0];

    // Prefer a name-ish column, then any non-identifier, and only fall back to
    // an id when the result genuinely has nothing else to show.
    const candidates = dimensions.filter((c) => c !== splitColumn);
    const labelColumn =
      candidates.find((c) => LABEL_HINT.test(c) && !ID_HINT.test(c)) ??
      candidates.find((c) => !ID_HINT.test(c)) ??
      candidates[0];

    if (!labelColumn) return null;

    const measureColumn = measures.find((c) => RATING_HINT.test(c)) ?? measures[0];
    const iconColumn = data.columns.find((c) => ICON_HINT.test(c));

    const descriptionColumn = data.columns.find(
      (c) => DESCRIPTION_HINT.test(c) && !PLUMBING.test(c) && c !== labelColumn,
    );

    // Anything already visible in the row would only be repeated in the detail.
    const used = new Set(
      [labelColumn, measureColumn, iconColumn, splitColumn, descriptionColumn].filter(Boolean),
    );
    const detailColumns = data.columns.filter(
      (c) => !used.has(c) && !PLUMBING.test(c) && DETAIL_HINT.test(c),
    );

    const li = indexOf(labelColumn);
    const mi = measureColumn ? indexOf(measureColumn) : -1;
    const ii = iconColumn ? indexOf(iconColumn) : -1;
    const si = splitColumn ? indexOf(splitColumn) : -1;
    const di = descriptionColumn ? indexOf(descriptionColumn) : -1;

    // First-seen order for the columns too — the query's ordering of the split
    // dimension is as deliberate as its ordering of the rows.
    const byName = new Map<string, Column>();
    for (const row of data.rows) {
      const key = si >= 0 ? text(row[si]) || '—' : humanise(labelColumn);
      let column = byName.get(key);
      if (!column) {
        column = { name: key, rows: [] };
        byName.set(key, column);
      }
      if (column.rows.length >= MAX_ROWS) continue;

      column.rows.push({
        label: text(row[li]),
        value: mi >= 0 ? row[mi] : null,
        icon: ii >= 0 ? text(row[ii]) : undefined,
        details: detailColumns
          .map((c) => ({ label: DETAIL_LABELS[c.toLowerCase()] ?? humanise(c), value: cell(row[indexOf(c)], 'exact') }))
          .filter((d) => d.value !== '—')
          // `publisher_name` and `publisher_id` both label as "Publisher" and
          // on Top Charts both carry the publisher's name, so without this the
          // row lists it twice.
          .filter((d, i, all) => all.findIndex((x) => x.label === d.label) === i)
          .slice(0, MAX_DETAILS),
        description: di >= 0 ? plainText(text(row[di])) || undefined : undefined,
      });
    }

    const all = [...byName.values()];
    const columns = all.slice(0, MAX_COLUMNS);

    // Stars only when the measure genuinely is a 0–5 rating. A "score" that
    // runs to 100 drawn as stars would be a lie about the scale.
    const values = data.rows.map((r) => (mi >= 0 ? r[mi] : null)).filter(isNumeric);
    const asStars =
      Boolean(measureColumn && RATING_HINT.test(measureColumn)) &&
      values.length > 0 &&
      Math.max(...values) <= 5;

    const dropped = all.length - columns.length;
    const note = dropped > 0
      ? `${dropped} more ${dropped === 1 ? 'column was' : 'columns were'} left off — the table view has everything.`
      : undefined;

    return { columns, asStars, measureName: measureColumn ? humanise(measureColumn) : '', note };
  }, [data]);

  if (!view || !view.columns.length) return <Empty />;

  return (
    <Plot note={view.note}>
      <div className="ranked" ref={scroller}>
        {view.columns.map((column) => (
          <section className="ranked__col" key={column.name}>
            {/* With one column the heading only repeats the tile title above it. */}
            {view.columns.length > 1 && <h5 className="ranked__head">{column.name}</h5>}
            <ol className="ranked__list">
              {column.rows.map((row, i) => {
                const key = `${column.name}::${i}`;
                return (
                  <Row
                    key={`${row.label}-${i}`}
                    row={row}
                    rank={i + 1}
                    asStars={view.asStars}
                    open={openRow === key}
                    onToggle={() => toggle(key)}
                  />
                );
              })}
            </ol>
          </section>
        ))}
      </div>
    </Plot>
  );
};
